/**
 * The responsive contract.
 *
 * Two scales, with a hard rule about which one is allowed to decide what:
 *
 *   VIEWPORT tiers decide *chrome* — is the sidebar a rail or a sheet, is the
 *   header condensed, is a modal centred or a bottom sheet. These are questions
 *   about the device.
 *
 *   PANEL tiers decide *content* — table or cards, how many columns survive,
 *   whether the invoice detail splits. These are questions about the box the
 *   content is in, and the box is the viewport minus chrome. Asking the window
 *   instead is what made 1024px render worse than 1023px: the rail appeared and
 *   took 15rem out of the panel, while the table's own rule read the window
 *   getting wider and asked for *more* room.
 *
 * The names below are Tailwind's, and they mean what Tailwind means by them:
 * `sm` is 640 and `lg` is 1024, so a utility and a stylesheet rule that quote
 * the same name land on the same pixel. `xs` (384) is the one addition, and it
 * is the tier this project registers as `--breakpoint-xs` in index.css.
 *
 * 768 is deliberately absent. It was the seam that let the sidebar be mobile
 * while the table was still desktop, and nothing keyed to it is a *layout*
 * boundary; the handful of `max-width: 767px` rules left in index.css are
 * spacing and touch-sizing, not structure.
 *
 * `floor` is not a breakpoint but the narrowest viewport the app supports:
 * 320px is not "xs minus a bit", it is the width at which fixed-size chrome has
 * to become something else rather than something smaller.
 */

/** Chrome only. `xs` mirrors `--breakpoint-xs`; the rest are Tailwind's own. */
export const VIEWPORT = {
  floor: 320,
  xs: 384,
  sm: 640,
  lg: 1024,
  xl: 1280,
};

/**
 * Content only. Mirrored by the `@container panel (...)` rules in index.css.
 *
 * Derived from what the invoice table actually needs at the type sizes this
 * design system ships, not from device widths:
 *
 *   cards    < 640   no table survives; the queue reads as a list of records
 *   compact  640–899 the essential columns only, ~120px of content each
 *   regular  900–1149 adds date, type, assignment, failure type
 *   full     >= 1150  every column, including PO and net
 *
 * "Essential" is five on most queues and six on Failed and In Query, which
 * promote the failure type and demote the processing status their base filter
 * has already pinned — see COLUMN_PRIORITY_BY_VARIANT in invoicePageConfig.
 *
 * An iPad portrait (768px viewport, sheet sidebar) lands in `compact` and gets
 * those readable columns where it used to get ten at ~43px of content apiece.
 */
export const PANEL = {
  cards: 0,
  compact: 640,
  regular: 900,
  full: 1150,
};

/** The invoice detail earns two columns at 54rem: a 540px document beside a 304px form. */
export const DETAIL_SPLIT_WIDTH = 864;

/**
 * Which density a panel of this width can carry.
 * `null` width means "not measured yet" — callers should hold rather than guess.
 */
export function getPanelTier(width) {
  if (width == null) return null;
  if (width >= PANEL.full) return "full";
  if (width >= PANEL.regular) return "regular";
  if (width >= PANEL.compact) return "compact";
  return "cards";
}
